import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Button, CircularProgress 
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { useTranslation } from "react-i18next";

const DailyConsumptionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consumption, setConsumption] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const apiUrl = "https://oven-backend.onrender.com";

  useEffect(() => {
    const fetchConsumption = async () => {
      try {
        const response = await axios.get(`${apiUrl}/dailyConsumption/${id}`);
        setConsumption(response.data);
      } catch (error) {
        console.error(t("error_fetching"), error);
      } finally {
        setLoading(false);
      }
    };
    fetchConsumption();
  }, [id, t]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box m="20px" sx={{ direction: isRTL ? "rtl" : "ltr", textAlign: isRTL ? "right" : "left" }}>
      <Typography variant="h4" fontWeight="bold">
        {t("daily_consumption_details")} 🍽️
      </Typography>

      <Box 
        mt={2} p={2} component={Paper} elevation={3} 
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Typography>{t("item")}: {consumption.item?.name || t("not_available")}</Typography>
        <Typography>{t("recorded_by")}: {consumption.recordedBy?.name || t("not_available")}</Typography>
        <Typography>{t("consumption_date")}: {new Date(consumption.consumptionDate).toLocaleDateString()}</Typography>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table dir={isRTL ? "rtl" : "ltr"}>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
              <TableCell sx={{ textAlign: isRTL ? "right" : "left" }}>
                <b>{t("consumed_amount")}</b>
              </TableCell>
              <TableCell sx={{ textAlign: isRTL ? "right" : "left" }}>
                <b>{t("notes")}</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ textAlign: isRTL ? "right" : "left" }}>
                {consumption.consumedAmount}
              </TableCell>
              <TableCell sx={{ textAlign: isRTL ? "right" : "left" }}>
                {consumption.notes || t("no_notes")}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={3} display="flex" gap={2} flexDirection={isRTL ? "row-reverse" : "row"}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<EditIcon />} 
          onClick={() => navigate(`/updateDailyConsumption/${id}`)}
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          {t("edit_consumption")}
        </Button>

        <Button 
          variant="contained" 
          color="secondary" 
          startIcon={<PrintIcon />} 
          onClick={() => window.print()}
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          {t("print_details")}
        </Button>

        <Button 
          variant="contained" 
          color="success" 
          startIcon={<PictureAsPdfIcon />}
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          {t("download_pdf")}
        </Button>
      </Box>
    </Box>
  );
};

export default DailyConsumptionDetails;
